import { FC, useState, useEffect } from 'react'
import { jingqueAdminApi, AnalyzeTask } from '@/api/jingqueAdminApi'

export const AnalyzeTasksPage: FC = () => {
  const [tasks, setTasks] = useState<AnalyzeTask[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize] = useState(20)
  const [total, setTotal] = useState(0)
  const [filterUserId, setFilterUserId] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [selected, setSelected] = useState<AnalyzeTask | null>(null)

  const loadTasks = async () => {
    setLoading(true)
    setError('')
    try {
      const res = await jingqueAdminApi.analyzeTasks.list(
        page,
        pageSize,
        filterUserId || undefined,
        filterStatus || undefined
      )
      setTasks(res.items)
      setTotal(res.total)
    } catch (e: any) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { loadTasks() }, [page, filterUserId, filterStatus])

  const statusColor = (s: string) => {
    switch (s) {
      case 'completed': return 'bg-green-100 text-green-700'
      case 'running': return 'bg-blue-100 text-blue-700'
      case 'failed': return 'bg-red-100 text-red-700'
      case 'pending': return 'bg-yellow-100 text-yellow-700'
      default: return 'bg-gray-100 text-gray-600'
    }
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-semibold text-gray-800">分析任务</h1>
        <div className="flex gap-2">
          <button onClick={loadTasks} className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg">刷新</button>
        </div>
      </div>

      <div className="flex gap-3 mb-4">
        <input
          value={filterUserId}
          onChange={e => { setFilterUserId(e.target.value); setPage(1) }}
          placeholder="按用户ID过滤"
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm w-48"
        />
        <select
          value={filterStatus}
          onChange={e => { setFilterStatus(e.target.value); setPage(1) }}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
        >
          <option value="">全部状态</option>
          <option value="pending">pending</option>
          <option value="running">running</option>
          <option value="completed">completed</option>
          <option value="failed">failed</option>
        </select>
      </div>

      {error && <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">{error}</div>}

      {loading ? (
        <div className="flex justify-center py-12">
          <svg className="animate-spin h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
          </svg>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left">Task ID</th>
                  <th className="px-4 py-3 text-left">用户</th>
                  <th className="px-4 py-3 text-left">模板</th>
                  <th className="px-4 py-3 text-left">状态</th>
                  <th className="px-4 py-3 text-left">进度</th>
                  <th className="px-4 py-3 text-left">创建时间</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map(t => (
                  <tr
                    key={t.id}
                    onClick={() => setSelected(t)}
                    className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                  >
                    <td className="px-4 py-3 font-mono text-xs text-gray-600">{t.taskId}</td>
                    <td className="px-4 py-3 text-gray-800">{t.userId}</td>
                    <td className="px-4 py-3 text-gray-600">{t.templateId}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded text-xs ${statusColor(t.status)}`}>{t.status}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{t.successCount}/{t.totalItems}</td>
                    <td className="px-4 py-3 text-gray-500">{formatDate(t.createdAt)}</td>
                  </tr>
                ))}
                {tasks.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-gray-400">暂无数据</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {total > pageSize && (
            <div className="flex justify-center gap-2 mt-6">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded disabled:opacity-50">上一页</button>
              <span className="px-3 py-1 text-sm text-gray-600">{page} / {Math.ceil(total / pageSize)}</span>
              <button disabled={page >= Math.ceil(total / pageSize)} onClick={() => setPage(p => p + 1)} className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded disabled:opacity-50">下一页</button>
            </div>
          )}
        </>
      )}

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-lg font-medium">任务详情</h2>
              <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-gray-500">Task ID:</span> <span className="font-mono ml-2">{selected.taskId}</span></div>
                <div><span className="text-gray-500">用户:</span> <span className="ml-2">{selected.userId}</span></div>
                <div><span className="text-gray-500">模板:</span> <span className="ml-2">{selected.templateId}</span></div>
                <div><span className="text-gray-500">状态:</span> <span className={`ml-2 px-2 py-0.5 rounded text-xs ${statusColor(selected.status)}`}>{selected.status}</span></div>
                <div><span className="text-gray-500">进度:</span> <span className="ml-2">{selected.successCount}/{selected.totalItems} ({selected.failedCount} 失败)</span></div>
                <div><span className="text-gray-500">创建:</span> <span className="ml-2">{formatDate(selected.createdAt)}</span></div>
              </div>
              {selected.errorMessage && <div className="p-3 bg-red-50 text-red-600 rounded-lg text-sm">{selected.errorMessage}</div>}
              {selected.variables && Object.keys(selected.variables).length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">变量</h3>
                  <div className="bg-gray-50 p-3 rounded-lg text-sm font-mono">
                    {Object.entries(selected.variables).map(([k, v]) => <div key={k}><span className="text-gray-500">{k}:</span> {String(v)}</div>)}
                  </div>
                </div>
              )}
              {selected.results && selected.results.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">结果</h3>
                  <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left">属性</th>
                        <th className="px-3 py-2 text-left">结果</th>
                        <th className="px-3 py-2 text-left">Tokens(p/c)</th>
                        <th className="px-3 py-2 text-left">轮数</th>
                        <th className="px-3 py-2 text-left">错误</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selected.results.map((r, i) => (
                        <tr key={i} className="border-t border-gray-100">
                          <td className="px-3 py-2 font-mono text-xs">{r.propertyName}</td>
                          <td className="px-3 py-2 text-gray-700 text-xs max-w-xs truncate">{r.result}</td>
                          <td className="px-3 py-2 text-gray-500 text-xs">{r.promptTokens}/{r.completionTokens}</td>
                          <td className="px-3 py-2 text-gray-500 text-xs">{r.agentRounds}</td>
                          <td className="px-3 py-2 text-red-500 text-xs">{r.error || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function formatDate(s: string) {
  if (!s) return '-'
  return new Date(s).toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai' })
}
